import { PlatformError } from './adapter.js';
const member = (p) => ({ runtimeId: p.runtimeId, persistentId: p.persistentId, displayName: p.displayName });
/**
 * In-memory meeting that behaves like Zoom in the ways the engine cares about:
 *  - runtime ids change on every open/close transition; persistent ids survive
 *  - leave + rejoin resets both ids
 *  - from the main session you only see participants in the main session
 *  - assignments persist across close; closing moves everyone to main;
 *    opening moves everyone to their assigned room (auto-move on)
 * Failure injection lets tests make any method fail N times or always.
 */
export class SimulatedAdapter {
    participants = [];
    rooms = [];
    state = 'closed';
    settings = null;
    failures = new Map();
    roomListeners = new Set();
    participantListeners = new Set();
    seq = 0;
    operatorLocation = 'main';
    role = 'host';
    /** Whether listRooms reports joined participants separately (gate question 3). */
    reportsJoined = false;
    calls = [];
    // ---- test controls ----------------------------------------------------
    join(displayName, opts = {}) {
        const p = {
            runtimeId: this.newRuntimeId(),
            persistentId: `P${++this.seq}`,
            displayName,
            assignedRoom: null,
            location: null,
            connected: true,
        };
        if (opts.email)
            p.email = opts.email;
        this.participants.push(p);
        this.emitParticipants();
        return p.runtimeId;
    }
    /** Simulate a drop and rejoin: new runtime id and new persistent id, assignment lost. */
    rejoin(displayName) {
        const p = this.participants.find((x) => x.displayName === displayName && x.connected);
        if (p)
            p.connected = false;
        return this.join(displayName);
    }
    leave(runtimeId) {
        const p = this.participants.find((x) => x.runtimeId === runtimeId);
        if (p)
            p.connected = false;
        this.emitParticipants();
    }
    /** Host moves someone by hand in Zoom's own panel (FR15). */
    manualMove(runtimeId, platformRoomId) {
        const p = this.live().find((x) => x.runtimeId === runtimeId);
        if (!p)
            throw new Error('no such participant');
        p.assignedRoom = platformRoomId;
        if (this.state === 'open')
            p.location = platformRoomId;
        this.emitRooms({ kind: 'participant_moved' });
    }
    failNext(method, times = 1, opts = {}) {
        this.failures.set(method, times);
        if (opts.retryable === false)
            this.failures.set(`${method}:nonretryable`, 1);
    }
    /** Current truth for assertions. */
    whereIs(displayName) {
        const p = this.live().find((x) => x.displayName === displayName);
        if (!p)
            throw new Error(`${displayName} not in meeting`);
        return p.location;
    }
    runtimeIdOf(displayName) {
        const p = this.live().find((x) => x.displayName === displayName);
        if (!p)
            throw new Error(`${displayName} not in meeting`);
        return p.runtimeId;
    }
    roomState() {
        return this.state;
    }
    // ---- PlatformAdapter --------------------------------------------------
    async getContext() {
        return { meetingId: 'sim-meeting', role: this.role, location: this.operatorLocation };
    }
    async listParticipants() {
        this.maybeFail('listParticipants');
        // From the main session you only see the main session.
        return this.live()
            .filter((p) => (this.operatorLocation === 'main' ? p.location === null : true))
            .map((p) => {
            const lp = { runtimeId: p.runtimeId, persistentId: p.persistentId, displayName: p.displayName, location: p.location === null ? 'main' : { roomId: p.location } };
            if (p.email)
                lp.email = p.email;
            return lp;
        });
    }
    async listRooms() {
        this.maybeFail('listRooms');
        const rooms = this.rooms.map((r) => {
            const pr = {
                platformRoomId: r.platformRoomId,
                name: r.name,
                assigned: this.live().filter((p) => p.assignedRoom === r.platformRoomId).map(member),
            };
            if (this.reportsJoined)
                pr.joined = this.live().filter((p) => p.location === r.platformRoomId).map(member);
            return pr;
        });
        return { state: this.state, rooms };
    }
    async createRooms(names) {
        this.maybeFail('createRooms', names);
        if (this.state === 'open')
            throw new PlatformError('Cannot create rooms while open', 'rooms_open', false);
        const created = names.map((name) => ({ platformRoomId: `bo-${++this.seq}`, name }));
        this.rooms.push(...created);
        this.emitRooms({ kind: 'rooms_changed' });
        return created;
    }
    async configureRooms(settings) {
        this.maybeFail('configureRooms', settings);
        this.settings = settings;
        return { applied: settings, unsupported: [] };
    }
    async assign(runtimeId, platformRoomId) {
        this.maybeFail('assign', runtimeId, platformRoomId);
        const p = this.live().find((x) => x.runtimeId === runtimeId);
        if (!p)
            throw new PlatformError(`Unknown participant ${runtimeId}`, 'unknown_participant', false);
        if (platformRoomId !== null && !this.rooms.some((r) => r.platformRoomId === platformRoomId)) {
            throw new PlatformError(`Unknown room ${platformRoomId}`, 'unknown_room', false);
        }
        p.assignedRoom = platformRoomId;
        if (this.state === 'open' && this.settings?.autoMoveIntoRooms)
            p.location = platformRoomId;
    }
    async openRooms() {
        this.maybeFail('openRooms');
        if (this.state === 'open')
            return;
        this.state = 'open';
        for (const p of this.live()) {
            p.runtimeId = this.newRuntimeId();
            if (this.settings?.autoMoveIntoRooms !== false)
                p.location = p.assignedRoom;
        }
        this.emitRooms({ kind: 'opened' });
        this.emitParticipants();
    }
    async closeRooms() {
        this.maybeFail('closeRooms');
        if (this.state === 'closed')
            return;
        this.state = 'closed';
        for (const p of this.live()) {
            p.runtimeId = this.newRuntimeId();
            p.location = null; // assignment persists; location does not
        }
        this.emitRooms({ kind: 'closed' });
        this.emitParticipants();
    }
    onRoomChange(cb) {
        this.roomListeners.add(cb);
        return () => this.roomListeners.delete(cb);
    }
    onParticipantChange(cb) {
        this.participantListeners.add(cb);
        return () => this.participantListeners.delete(cb);
    }
    // ---- internals --------------------------------------------------------
    live() {
        return this.participants.filter((p) => p.connected);
    }
    newRuntimeId() {
        return `rt-${++this.seq}`;
    }
    maybeFail(method, ...args) {
        this.calls.push({ method, args });
        const n = this.failures.get(method) ?? 0;
        if (n > 0) {
            this.failures.set(method, n - 1);
            const nonRetryable = this.failures.get(`${method}:nonretryable`) === 1;
            if (nonRetryable)
                this.failures.delete(`${method}:nonretryable`);
            throw new PlatformError(`Injected failure in ${method}`, 'injected', !nonRetryable);
        }
    }
    emitRooms(e) {
        for (const l of this.roomListeners)
            l(e);
    }
    emitParticipants() {
        for (const l of this.participantListeners)
            l();
    }
}
//# sourceMappingURL=simulated.js.map